import { create } from 'zustand';
import { supabase } from './supabase';
import type { Bike, Case, Client, Document, Payment } from './supabase';

interface AppState {
  cases: Case[];
  bikes: Bike[];
  clients: Client[];
  documents: Document[];
  payments: Payment[];
  loading: boolean;
  error: string | null;
  fetchCases: () => Promise<void>;
  fetchBikes: () => Promise<void>;
  fetchClients: () => Promise<void>;
  createCase: (caseData: Partial<Case>) => Promise<Case | null>;
  updateCase: (id: string, updates: Partial<Case>) => Promise<void>;
  createBike: (bikeData: Partial<Bike>) => Promise<Bike | null>;
  updateBike: (id: string, updates: Partial<Bike>) => Promise<void>;
  updateBikeStatus: (id: string, status: string) => Promise<void>;
  processDocument: (file: File, caseId: string) => Promise<void>;
  recordPayment: (paymentData: Partial<Payment>) => Promise<void>;
}

export const useStore = create<AppState>((set, get) => ({
  cases: [],
  bikes: [],
  clients: [],
  documents: [],
  payments: [],
  loading: false,
  error: null,

  fetchCases: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('*, client:clients(*), bike:bikes(*), documents(*), payments(*)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ cases: data || [] });
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  fetchBikes: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('bikes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ bikes: data || [] });
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  fetchClients: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ clients: data || [] });
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  createCase: async (caseData) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('cases')
        .insert([{ ...caseData, status: 'new_matter' }])
        .select()
        .single();

      if (error) throw error;
      
      const cases = get().cases;
      set({ cases: [data, ...cases] });
      
      return data;
    } catch (error) {
      set({ error: error.message });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  updateCase: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('cases')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      const cases = get().cases.map(c => 
        c.id === id ? { ...c, ...updates } : c
      );
      set({ cases });
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  createBike: async (bikeData) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('bikes')
        .insert([{ ...bikeData, status: 'available' }])
        .select()
        .single();

      if (error) throw error;
      
      const bikes = get().bikes;
      set({ bikes: [data, ...bikes] });
      
      return data;
    } catch (error) {
      set({ error: error.message });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  updateBike: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      console.log(' [updateBike] Updating bike in store:', id);
      console.log(' [updateBike] Original update data:', updates);

      // Defensive: Clean and prepare update data
      const cleanUpdates = { ...updates };

      // Defensive numeric and object handling
      if ('daily_rate' in updates) {
        if (updates.daily_rate === null || updates.daily_rate === undefined) {
          cleanUpdates.daily_rate = 0;
        } else if (typeof updates.daily_rate === 'number') {
          cleanUpdates.daily_rate = updates.daily_rate;
        } else {
          const parsed = parseFloat(updates.daily_rate);
          cleanUpdates.daily_rate = isNaN(parsed) ? 0 : parsed;
        }
      }
      if ('rate_a' in updates) {
        if (updates.rate_a === null) {
          cleanUpdates.rate_a = null;
        } else if (updates.rate_a === undefined) {
          delete cleanUpdates.rate_a;
        } else if (typeof updates.rate_a === 'number') {
          cleanUpdates.rate_a = updates.rate_a;
        } else {
          const parsed = parseFloat(updates.rate_a);
          cleanUpdates.rate_a = isNaN(parsed) ? null : parsed;
        }
      }
      if ('rate_b' in updates) {
        if (updates.rate_b === null) {
          cleanUpdates.rate_b = null;
        } else if (updates.rate_b === undefined) {
          delete cleanUpdates.rate_b;
        } else if (typeof updates.rate_b === 'number') {
          cleanUpdates.rate_b = updates.rate_b;
        } else {
          const parsed = parseFloat(updates.rate_b);
          cleanUpdates.rate_b = isNaN(parsed) ? null : parsed;
        }
      }
      if ('insurance_details' in updates) {
        cleanUpdates.insurance_details = updates.insurance_details || {};
      }
      if ('service_notes' in updates && updates.service_notes !== null) {
        if (typeof updates.service_notes === 'string') {
          cleanUpdates.service_notes = updates.service_notes.split('\n').filter(Boolean);
        } else if (!Array.isArray(updates.service_notes)) {
          cleanUpdates.service_notes = [];
        }
      }
      console.log(' [updateBike] Cleaned update data:', cleanUpdates);
      // Log the Supabase client state if possible
      console.log(' [updateBike] Supabase client:', supabase);
      // --- FULL RESPONSE LOGGING ---
      console.log('🟦 [updateBike] Will call supabase.from("bikes").update:', cleanUpdates, 'where id =', id);
    const response = await supabase
      .from('bikes')
      .update(cleanUpdates)
      .eq('id', id)
      .select();
    console.log('🟦 [updateBike] Supabase response (full):', JSON.stringify(response, null, 2));
    if (response && typeof response === 'object') {
      Object.keys(response).forEach(key =>
        console.log(`[updateBike] Supabase response field: ${key}`, response[key])
      );
    }
      const { data, error, status, statusText } = response;
      if (error || !data) {
        const fallbackMsg = (error && error.message) || statusText || 'Unknown error updating bike';
        console.error('[updateBike] Error updating bike:', error || fallbackMsg, { data, status, statusText });
        set({ error: fallbackMsg });
        throw new Error(fallbackMsg);
      }
      // Update the bike in the local state immediately with the returned data
      if (data && data.length > 0) {
        const updatedBike = data[0];
        const bikes = get().bikes.map(b => b.id === id ? updatedBike : b);
        console.log('[updateBike] Updated bike in store with data from server', updatedBike);
        set({ bikes });
      } else {
        // Fallback to local update if no data returned (shouldn't happen)
        const bikes = get().bikes.map(b => {
          if (b.id === id) {
            return {
              ...b,
              model: cleanUpdates.model || b.model,
              rego_number: cleanUpdates.rego_number || b.rego_number,
              daily_rate: typeof cleanUpdates.daily_rate === 'number' ? cleanUpdates.daily_rate : b.daily_rate,
              status: cleanUpdates.status || b.status,
              notes: cleanUpdates.notes !== undefined ? cleanUpdates.notes : b.notes,
              insurance_details: cleanUpdates.insurance_details || b.insurance_details
            };
          }
          return b;
        });
        console.log('[updateBike] Updated bikes in store with local data');
        set({ bikes });
        get().fetchBikes();
      }
    } catch (error: unknown) {
      console.error(' [updateBike] Caught error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error updating bike';
      set({ error: errorMessage });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateBikeStatus: async (id, status) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('bikes')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      const bikes = get().bikes.map(b => 
        b.id === id ? { ...b, status } : b
      );
      set({ bikes });
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  processDocument: async (file: File, caseId: string) => {
    set({ loading: true, error: null });
    try {
      const { createWorker } = await import('tesseract.js');
      const worker = await createWorker();
      
      const result = await worker.recognize(file);
      await worker.terminate();

      const { error } = await supabase
        .from('documents')
        .insert([{
          case_id: caseId,
          filename: file.name,
          type: 'claim',
          ocr_data: result.data,
        }]);

      if (error) throw error;

      await get().fetchCases();
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  recordPayment: async (paymentData) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('payments')
        .insert([paymentData]);

      if (error) throw error;

      await get().fetchCases();
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },
}));